import { COMPILED_COLLISION_CELLS_PER_TILE } from "../document/types.ts";

/**
 * Frame footprints (ADR-040 step 2, ADR-046): the ground-contact pixels of a frame, derived from its art
 * by a tool or authored by hand and carried in the compile context, stamped onto the collision grid
 * wherever the frame is placed as a prop or painted as a tile.
 */

/**
 * Where a frame sits on the map: its anchor cell (fractional for pixel-placed props) and mirroring. A
 * `PropInstance` satisfies it; a painted tile is its cell with its mirroring (ADR-089).
 */
export interface FootprintPlacement {
  readonly x: number;
  readonly y: number;
  readonly flipX: boolean;
  readonly flipY: boolean;
}

/**
 * One frame's footprint: the frame size and base row, and horizontal runs of opaque pixels in frame
 * coordinates. Each run is `[rowsAboveBase, start, end]`: `rowsAboveBase` 0 is the base row itself (the
 * last opaque row), `start` and `end` inclusive columns.
 *
 * `passage` is the frame's deck (ADR-017's amendment of 2026-10-05): runs in the same encoding, their rows
 * counted from the frame's bottom row whatever `base` is, under which a placed prop opens the ground the
 * terrain's rules block. `surface` is what that deck is made of, a name the game lists and Daedalus reads nothing
 * into; it names the tiles the passage lies in (`compile/surfaces.ts`). Both are absent on a frame with no deck.
 */
export interface PropFootprint {
  readonly w: number;
  readonly h: number;
  readonly base: number;
  readonly runs: readonly (readonly [number, number, number])[];
  readonly passage?: readonly (readonly [number, number, number])[];
  readonly surface?: string;
}

/**
 * The least of a cell an art run must cover, in pixels along each axis, to mark it, for a stamp at the 8 px
 * authoring cell; a cell covered less is still marked when it is the most covered cell of its column, so a rail
 * thinner than this keeps its collision. `footprintBlocks`, and so the default activation ring, read it.
 */
export const AUTHORING_MIN_COVER = 3;

/**
 * The least cover of a stamp at the 4 px compiled cell: half of the cell along each axis (ADR-113 decision 5,
 * the coordinator's decision).
 */
export const COMPILED_MIN_COVER = 2;

const COMPILED_CELL_PIXELS = 4;

/**
 * The least cover that marks a cell for a grid of this many cells a tile: `COMPILED_MIN_COVER` at the compiled
 * grid, `AUTHORING_MIN_COVER` at any coarser one.
 *
 * @param cellsPerTile Collision cells per tile of the grid being stamped.
 * @returns The least pixels a run must cover of a cell, along each axis, to mark it.
 */
export function minCoverFor(cellsPerTile: number): number {
  return cellsPerTile >= COMPILED_COLLISION_CELLS_PER_TILE ? COMPILED_MIN_COVER : AUTHORING_MIN_COVER;
}

/**
 * The least cover that marks a cell of this many pixels: `COMPILED_MIN_COVER` for the 4 px compiled cell and finer,
 * `AUTHORING_MIN_COVER` for any coarser one, as `minCoverFor` gives for the same grid.
 *
 * @param cellPixels The cell's side in pixels.
 * @returns The least pixels a run must cover of a cell, along each axis, to mark it.
 */
export function minCoverForCell(cellPixels: number): number {
  return cellPixels <= COMPILED_CELL_PIXELS ? COMPILED_MIN_COVER : AUTHORING_MIN_COVER;
}

/**
 * Sub-pixel steps an edge is held to before it is rounded to a cell, so an edge carried through tiles with
 * floating-point noise (a feet point less half the frame's width, times the tile size) rounds as its exact
 * pixel does at a tie.
 */
const EDGE_STEPS = 1024;

/**
 * The collision cell edge nearest a pixel edge, counted in cells from the grid's origin, a tie going right
 * (or down). The rounding every laid footprint and every set of activation cells share (`footprintCorner`); it allocates
 * nothing, for callers on a hot path.
 *
 * @param pixels The edge in pixels.
 * @param cellPixels Pixels per collision cell.
 * @returns The nearest cell edge, in cells.
 */
export function nearestCellEdge(pixels: number, cellPixels: number): number {
  return Math.round(Math.round(pixels * EDGE_STEPS) / EDGE_STEPS / cellPixels);
}

/**
 * Where a footprint is laid on the collision cells (ADR-040 amendment "the stamp lays a footprint on the
 * cells", P1.6-T78): the frame's left edge, or its right edge when mirrored, and its bottom edge, each
 * rounded to the nearest cell edge (`nearestCellEdge`). The footprint panel draws a frame with that corner
 * on a cell corner, so a footprint laid here blocks exactly the panel's cells wherever the art sits; the
 * collision moves at most half a cell from the art on each axis. Mirrored, the art is mirrored inside the
 * frame and the frame then laid by its right edge, where the mirrored cells start, so a frame whose width is
 * not whole cells blocks the panel's cells mirrored.
 *
 * @param left The frame's left edge in pixels, as the art is drawn.
 * @param bottom The frame's bottom edge in pixels, as the art is drawn.
 * @param frameWidth The frame's width in pixels.
 * @param flipX Whether the frame is mirrored.
 * @param cellPixels Pixels per collision cell.
 * @returns The frame's left and bottom edges in pixels as the footprint is laid; `left + frameWidth` is a
 *   cell edge when mirrored, `left` otherwise.
 * @example footprintCorner(3228, 4116, 96, false, 8) is { left: 3232, bottom: 4120 }.
 */
export function footprintCorner(left: number, bottom: number, frameWidth: number, flipX: boolean, cellPixels: number): { left: number; bottom: number } {
  const reach = flipX ? frameWidth : 0;
  return { left: nearestCellEdge(left + reach, cellPixels) * cellPixels - reach, bottom: nearestCellEdge(bottom, cellPixels) * cellPixels };
}

/**
 * Where a frame's own `[column, rowsUp]` cells lie once laid on the collision cells as its footprint is
 * (`footprintCorner`): the left edge of column 0 and the bottom of row 0, in pixels, both on cell edges. The
 * space is `ceil(frameWidth / cellPixels)` columns wide; mirrored, its right edge is the frame's laid right
 * edge, so cells mirrored across those columns (activation cells, ADR-053) land where the mirrored footprint
 * does.
 *
 * @param left The frame's left edge in pixels, as the art is drawn.
 * @param bottom The frame's bottom edge in pixels, as the art is drawn.
 * @param frameWidth The frame's width in pixels.
 * @param flipX Whether the frame is mirrored.
 * @param cellPixels Pixels per collision cell.
 * @returns Column 0's left edge and row 0's bottom edge in pixels.
 */
export function frameCellSpace(left: number, bottom: number, frameWidth: number, flipX: boolean, cellPixels: number): { left: number; bottom: number } {
  const corner = footprintCorner(left, bottom, frameWidth, flipX, cellPixels);
  return { left: flipX ? corner.left + frameWidth - Math.ceil(frameWidth / cellPixels) * cellPixels : corner.left, bottom: corner.bottom };
}

/**
 * The pixel row, counted from the frame's top, at which a run of a footprint is stamped when the frame is
 * mirrored top to bottom (ADR-040 amendment of 2026-10-07): the row the art's mirror puts it on, for drawn cells
 * and derived runs alike. Upright, a run `rows` above the base lies at `base - 1 - rows`.
 *
 * @param footprint The frame's footprint.
 * @param rows The run's rows above the footprint's base.
 * @returns The mirrored row from the frame's top.
 */
function mirroredRow(footprint: PropFootprint, rows: number): number {
  return footprint.h - footprint.base + rows;
}

/**
 * ORs a frame's footprint into a collision grid. The frame's bottom-left sits on the bottom-left of the
 * anchor cell `(x, y)`, laid on the collision cells by `footprintCorner` (its left edge, or its right edge
 * when mirrored, and its bottom edge rounded to the nearest cell edge), so wherever the prop's art sits the
 * stamp marks exactly the cells `footprintBlocks` gives, shifted by whole cells, and the base row lies
 * `h - base` pixels above the laid bottom edge. A cell is
 * marked when the art covers at least `minCover` pixels of it along the row and at least `minCover`
 * of its pixel rows, or when it holds the most rows of the unbroken band of art passing through its
 * column, so a band that straddles cell rows does not grow by a row and a rail thinner than a cell still
 * blocks. A horizontal flip mirrors the runs about the frame's middle column; a vertical flip mirrors them top to
 * bottom inside the frame's box, as the art is drawn (`mirroredRow`), the frame still laid from its bottom edge.
 *
 * @param grid Collision grid, `width * cells` by `height * cells`.
 * @param width Map width in tiles.
 * @param height Map height in tiles.
 * @param footprint The frame's footprint.
 * @param prop The frame's placement.
 * @param tileSize Tile size in pixels.
 * @param cells Collision cells per tile.
 * @param value Byte to OR in.
 * @param minCover The least pixels of a cell the art must cover, along the row and in rows, to mark it;
 *   `AUTHORING_MIN_COVER` (3) for a stamp at the 8 px cell, `COMPILED_MIN_COVER` (2) at the 4 px cell.
 */
export function stampFootprint(grid: Uint8Array, width: number, height: number, footprint: PropFootprint, prop: FootprintPlacement, tileSize: number, cells: number, value: number, minCover: number = AUTHORING_MIN_COVER): void {
  const cellPixels = tileSize / cells;
  const gridWidth = width * cells;
  const gridHeight = height * cells;
  const corner = footprintCorner(prop.x * tileSize, (prop.y + 1) * tileSize, footprint.w, prop.flipX, cellPixels);
  const left = corner.left;
  const top = corner.bottom - footprint.h;
  const chains = new Map<number, { lastRow: number; counts: Map<number, number> }>();
  const flush = (chain: { counts: Map<number, number> }): void => {
    let most = 0;
    for (const count of chain.counts.values()) {
      most = Math.max(most, count);
    }
    for (const [index, count] of chain.counts) {
      if (count >= minCover || count === most) {
        grid[index] = (grid[index] as number) | value;
      }
    }
  };
  const runs = [...footprint.runs].sort((a, b) => a[0] - b[0]);
  for (const [rows, start, end] of runs) {
    const py = top + (prop.flipY ? mirroredRow(footprint, rows) : footprint.base - 1 - rows);
    const cy = Math.floor(py / cellPixels);
    if (cy < 0 || cy >= gridHeight) {
      continue;
    }
    const first = left + (prop.flipX ? footprint.w - 1 - end : start);
    const last = left + (prop.flipX ? footprint.w - 1 - start : end);
    const length = last - first + 1;
    const centre = Math.floor((first + last) / 2 / cellPixels);
    const cx0 = Math.max(0, Math.floor(first / cellPixels));
    const cx1 = Math.min(gridWidth - 1, Math.floor(last / cellPixels));
    for (let cx = cx0; cx <= cx1; cx += 1) {
      const overlap = Math.min(last, (cx + 1) * cellPixels - 1) - Math.max(first, cx * cellPixels) + 1;
      if (overlap < minCover && !(length < minCover && cx === centre)) {
        continue;
      }
      let chain = chains.get(cx);
      if (chain !== undefined && rows !== chain.lastRow && rows !== chain.lastRow + 1) {
        flush(chain);
        chain = undefined;
      }
      if (chain === undefined) {
        chain = { lastRow: rows, counts: new Map() };
        chains.set(cx, chain);
      }
      chain.lastRow = rows;
      const index = cy * gridWidth + cx;
      chain.counts.set(index, (chain.counts.get(index) ?? 0) + 1);
    }
  }
  for (const chain of chains.values()) {
    flush(chain);
  }
}

/**
 * The collision cells a footprint blocks, as `stampFootprint` marks them in play: the cells the footprint
 * editor draws and a copy writes (ADR-040 amendment "footprints mark cells by coverage", P1.6-T11). The stamp
 * lays every placed frame with its corner on a cell corner (`footprintCorner`), so a prop anywhere blocks
 * exactly these cells, shifted by whole cells, and mirrored about the frame's right edge when mirrored.
 * Mirrored top to bottom, they are the cells the stamp marks for the frame so mirrored (`mirroredRow`).
 *
 * @param footprint The footprint.
 * @param cellPixels Pixels per collision cell.
 * @param flipY Whether the frame is mirrored top to bottom.
 * @param minCover The least cover that marks a cell, as `stampFootprint` takes it; `minCoverForCell` of the cell by
 *   default: the authoring cell's, which the default activation ring reads, and the 4 px cell's for a stamp at that cell.
 * @returns `[column, rowsUp]` cells from the frame's bottom-left, row-major from the bottom.
 */
export function footprintBlocks(footprint: PropFootprint, cellPixels: number, flipY = false, minCover: number = minCoverForCell(cellPixels)): [number, number][] {
  const columns = Math.ceil(footprint.w / cellPixels);
  const rows = Math.ceil(footprint.h / cellPixels);
  const grid = new Uint8Array(columns * rows);
  stampFootprint(grid, columns, rows, footprint, { x: 0, y: rows - 1, flipX: false, flipY }, cellPixels, 1, 1, minCover);
  const cells: [number, number][] = [];
  for (let up = 0; up < rows; up += 1) {
    for (let column = 0; column < columns; column += 1) {
      if (grid[(rows - 1 - up) * columns + column] !== 0) {
        cells.push([column, up]);
      }
    }
  }
  return cells;
}

/**
 * Marks the cells a placed frame's passage lies on (ADR-017's amendment of 2026-10-05), laid and covered exactly
 * as `stampFootprint` lays the frame's blocking runs, so a deck drawn in the footprint editor opens the cells
 * drawn there wherever the prop stands, mirrored with the prop as the blocking runs are.
 *
 * @param grid Grid at cell resolution to OR into, `width * cells` by `height * cells`.
 * @param width Map width in tiles.
 * @param height Map height in tiles.
 * @param footprint The frame's footprint; one with no passage marks nothing.
 * @param prop The frame's placement.
 * @param tileSize Tile size in pixels.
 * @param cells Collision cells per tile.
 * @param value Byte to OR in.
 * @param minCover The least cover that marks a cell, as `stampFootprint` takes it.
 */
export function stampPassage(grid: Uint8Array, width: number, height: number, footprint: PropFootprint, prop: FootprintPlacement, tileSize: number, cells: number, value: number, minCover: number = AUTHORING_MIN_COVER): void {
  const passage = footprint.passage;
  if (passage === undefined || passage.length === 0) {
    return;
  }
  stampFootprint(grid, width, height, { w: footprint.w, h: footprint.h, base: footprint.h, runs: passage }, prop, tileSize, cells, value, minCover);
}

/**
 * The collision cells a footprint's passage opens, as `stampPassage` marks them in play: the cells the footprint
 * editor draws in the open colour.
 *
 * @param footprint The footprint.
 * @param cellPixels Pixels per collision cell.
 * @param minCover The least cover that marks a cell, as `stampFootprint` takes it; `minCoverForCell` of the cell by default.
 * @returns `[column, rowsUp]` cells from the frame's bottom-left, row-major from the bottom; none for a
 *   footprint with no passage.
 */
export function footprintOpens(footprint: PropFootprint, cellPixels: number, minCover: number = minCoverForCell(cellPixels)): [number, number][] {
  return footprint.passage === undefined || footprint.passage.length === 0 ? [] : footprintBlocks({ w: footprint.w, h: footprint.h, base: footprint.h, runs: footprint.passage }, cellPixels, false, minCover);
}

/**
 * The pixels a footprint covers, keyed by column and row up from the frame's bottom, which is all a stamp
 * reads besides the frame's width.
 *
 * @param footprint The footprint.
 * @returns One key per covered pixel.
 */
function coveredPixels(footprint: PropFootprint): Set<string> {
  const pixels = new Set<string>();
  for (const [rows, start, end] of footprint.runs) {
    const up = footprint.h - footprint.base + rows;
    for (let x = start; x <= end; x += 1) {
      pixels.add(`${x},${up}`);
    }
  }
  return pixels;
}

/**
 * Whether two footprints block and open the same wherever their frame is placed: the same width, which a
 * mirrored placement reflects about, the same pixels counted from the frame's bottom, however the runs are split,
 * ordered or based, the same pixels of passage, and the same surface on a deck.
 *
 * @param a One footprint.
 * @param b The other.
 * @returns True when they stamp alike at every placement.
 */
export function sameFootprint(a: PropFootprint, b: PropFootprint): boolean {
  if (a.w !== b.w) {
    return false;
  }
  const alike = (left: Set<string>, right: Set<string>): boolean => left.size === right.size && [...left].every((key) => right.has(key));
  const deckOf = (footprint: PropFootprint): Set<string> => coveredPixels({ w: footprint.w, h: footprint.h, base: footprint.h, runs: footprint.passage ?? [] });
  const decks = [deckOf(a), deckOf(b)] as const;
  const surfaces = [decks[0].size === 0 ? undefined : a.surface, decks[1].size === 0 ? undefined : b.surface];
  return alike(coveredPixels(a), coveredPixels(b)) && alike(decks[0], decks[1]) && surfaces[0] === surfaces[1];
}
